import type { ErrorComponentProps } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { Notice } from '@/components/Notice';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { routeErrorMessage } from '@/lib/routeErrorMessage';

/**
 * The route-level error boundary — `FE-04.11`, item text and
 * `entry-surfaces.md` D5.
 *
 * Renders a `Notice tone="bad"` — never an `EmptyState`. An `EmptyState`
 * *replaces* a surface's content; this boundary catches a render error
 * anywhere below the root route (`routes/__root.tsx` sets it once, and a
 * React error boundary catches every descendant regardless of how many
 * intermediate route matches sit between it and the throw), and the shell
 * chrome around it — the header, the skip link — is still there and still
 * usable. Erasing that with a full-page EmptyState would be the wrong
 * primitive for a failure that left the rest of the page intact.
 *
 * `Notice`'s own `tone="bad"` sets `role="alert"`, so this **does not**
 * call `announce()` — doing both is the double-announcement D11 forbids.
 *
 * The message shown is `routeErrorMessage(error)`, not `error.message`
 * verbatim: see that file for why an arbitrary thrown value is not safe to
 * show a member directly.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const { t } = useInterfaceLanguage();

  return (
    <Notice
      tone="bad"
      title={t('route.error.title')}
      body={routeErrorMessage(error, t('route.error.genericBody'))}
      action={
        <Button variant="secondary" onClick={reset}>
          {t('route.error.retry')}
        </Button>
      }
    />
  );
}
